import React, { useEffect, useState } from "react";
import "../styles/bookings.css";
import {
  Plus,
  Search,
  Calendar,
  MapPin,
  Users,
  UserCircle,
  AlertCircle,
  Globe,
} from "lucide-react";
import CreateBookingDialog from "../components/bookings/CreateBookingDialog";
import axios from "axios";

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [filterBookingType, setFilterBookingType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch bookings from backend
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching bookings...");
      
      // Use explicit base URL
      const baseURL = "https://crmapi.zauntrip.com";
      const response = await axios.get(`${baseURL}/api/bookings`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log("Bookings response:", response.data);
      
      if (response.data.success) {
        // Handle both array format and object format
        if (Array.isArray(response.data.data)) {
          setBookings(response.data.data);
        } else if (response.data.data && Array.isArray(response.data.data.data)) {
          // If backend returns { data: { data: [...] } }
          setBookings(response.data.data.data);
        } else {
          setBookings([]);
        }
      } else {
        throw new Error(response.data.message || "Failed to load bookings");
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          "Failed to load bookings. Please check if the server is running.";
      setError(errorMessage);
      
      // For development, use mock data
      if (process.env.NODE_ENV === 'development') {
        console.log("Using mock bookings data for development");
        setBookings(mockBookings);
        setError(null); // Clear error when using mock data
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async (bookingData) => {
    try {
      setActionLoading(true);
      console.log("Creating booking with data:", bookingData);
      
      const baseURL = "https://crmapi.zauntrip.com";
      const response = await axios.post(`${baseURL}/api/bookings`, bookingData, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log("Create booking response:", response.data);
      
      if (response.data.success) {
        alert("Booking created successfully!");
        setShowCreateDialog(false);
        fetchBookings(); // Refresh the list
      } else {
        throw new Error(response.data.message || "Failed to create booking");
      }
    } catch (err) {
      console.error("Error creating booking:", err);
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          "Failed to create booking. Please check the console for details.";
      alert(`Error: ${errorMessage}`);
      throw err; // Re-throw to let dialog handle it
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateBooking = async (bookingData) => {
    try {
      setActionLoading(true);
      console.log("Updating booking:", selectedBooking._id, "with data:", bookingData);
      
      const baseURL = "https://crmapi.zauntrip.com";
      const response = await axios.put(
        `${baseURL}/api/bookings/${selectedBooking._id}`, 
        bookingData,
        {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      console.log("Update booking response:", response.data);
      
      if (response.data.success) {
        alert("Booking updated successfully!");
        setShowEditDialog(false);
        setSelectedBooking(null);
        fetchBookings(); // Refresh the list
      } else {
        throw new Error(response.data.message || "Failed to update booking");
      }
    } catch (err) {
      console.error("Error updating booking:", err);
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          "Failed to update booking. Please check the console for details.";
      alert(`Error: ${errorMessage}`);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) {
      return;
    }
    
    try {
      setActionLoading(true);
      console.log("Deleting booking:", bookingId);
      
      const baseURL = "https://crmapi.zauntrip.com";
      const response = await axios.delete(`${baseURL}/api/bookings/${bookingId}`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log("Delete booking response:", response.data);
      
      if (response.data.success) {
        alert("Booking deleted successfully!");
        fetchBookings(); // Refresh the list
      } else {
        throw new Error(response.data.message || "Failed to delete booking");
      }
    } catch (err) {
      console.error("Error deleting booking:", err);
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          "Failed to delete booking. Please check the console for details.";
      alert(`Error: ${errorMessage}`);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredBookings = bookings.filter((b) => {
    // Search filter - handle nested or flat data structure
    const clientName = b.clientId?.fullName || b.clientName || "Unknown Client";
    const agentFirstName = b.agentId?.firstName || "";
    const agentLastName = b.agentId?.lastName || "";
    const agentName = `${agentFirstName} ${agentLastName}`.trim();
    const destination = b.clientId?.destinationInterest || b.destination || "";
    const bookingId = b.bookingId || b._id || "";
    
    const matchesSearch =
      clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.specialRequests && b.specialRequests.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      filterStatus === "all" || b.bookingStatus === filterStatus;

    const matchesPayment =
      filterPayment === "all" || b.paymentStatus === filterPayment;

    const matchesBookingType =
      filterBookingType === "all" || b.bookingType === filterBookingType;

    return matchesSearch && matchesStatus && matchesPayment && matchesBookingType;
  });

  // Calculate amount due (frontend fallback)
  const calculateAmountDue = (booking) => {
    if (booking.amountDue !== undefined && booking.amountDue !== null) {
      return parseFloat(booking.amountDue) || 0;
    }
    const total = parseFloat(booking.totalAmount) || 0;
    const paid = parseFloat(booking.amountPaid) || 0;
    return total - paid;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Format travel dates range
  const formatTravelDates = (startDate, endDate) => {
    const start = formatDate(startDate);
    if (!endDate || endDate === startDate) return start;
    const end = formatDate(endDate);
    return `${start} - ${end}`;
  };

  // Get agent full name
  const getAgentFullName = (agent) => {
    if (!agent) return "No Agent";
    if (typeof agent === 'string') return agent;
    return `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || "Unknown Agent";
  };

  // Get client full name
  const getClientFullName = (client) => {
    if (!client) return "Unknown Client";
    if (typeof client === 'string') return client;
    return client.fullName || client.email || "Unknown Client";
  };

  return (
    <div className="bookings-page">
      <div className="bookings-header">
        <div>
          <h1>Bookings</h1>
          <p>Manage travel bookings</p>
        </div>
        <button 
          className="btn-primary" 
          onClick={() => setShowCreateDialog(true)}
          disabled={actionLoading}
        >
          <Plus size={16} /> New Booking
        </button>
      </div>

      {/* Debug info for development */}
      {process.env.NODE_ENV === 'development' && error && (
        <div className="debug-info">
          <AlertCircle size={16} />
          <small>Debug: {error}. Using mock data for development.</small>
        </div>
      )}

      {/* Filters */}
      <div className="bookings-filters">
        <div className="search-box">
          <Search size={16} />
          <input
            placeholder="Search by name, Booking ID, destination, or agent..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={actionLoading}
          />
        </div>

        <select
          value={filterBookingType}
          onChange={(e) => setFilterBookingType(e.target.value)}
          disabled={actionLoading}
        >
          <option value="all">All Types</option>
          <option value="Domestic">Domestic</option>
          <option value="International">International</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          disabled={actionLoading}
        >
          <option value="all">All Status</option>
          <option value="Confirmed">Confirmed</option>
          <option value="Pending">Pending</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        <select
          value={filterPayment}
          onChange={(e) => setFilterPayment(e.target.value)}
          disabled={actionLoading}
        >
          <option value="all">All Payments</option>
          <option value="Pending">Pending</option>
          <option value="Partial">Partial</option>
          <option value="Paid">Paid</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <p>Loading bookings...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && process.env.NODE_ENV !== 'development' && (
        <div className="error-state">
          <AlertCircle size={24} />
          <p>{error}</p>
          <p><small>Make sure your backend server is running on https://crmapi.zauntrip.com</small></p>
          <button onClick={fetchBookings} className="btn-outline" disabled={actionLoading}>
            Retry
          </button>
        </div>
      )}

      {/* Booking Cards */}
      {!loading && !error && (
        <div className="bookings-grid">
          {filteredBookings.map((b) => {
            const amountDue = calculateAmountDue(b);
            const agentName = getAgentFullName(b.agentId);
            const clientName = getClientFullName(b.clientId);
            
            return (
              <div key={b._id} className="booking-card">
                <div className="booking-header">
                  <div>
                    <h3>{clientName}</h3>
                    <span className="booking-id">Booking ID: {b.bookingId || b._id?.substring(0, 8) || 'N/A'}</span>
                  </div>
                  <div className="status-badges">
                    <span className={`badge type ${b.bookingType?.toLowerCase() || 'domestic'}`}>
                      <Globe size={12} /> {b.bookingType || "Domestic"}
                    </span>
                    <span className={`badge status ${(b.bookingStatus || 'Pending').toLowerCase()}`}>
                      {b.bookingStatus || "Pending"}
                    </span>
                    <span className={`badge payment ${(b.paymentStatus || 'Pending').toLowerCase()}`}>
                      {b.paymentStatus || "Pending"}
                    </span>
                  </div>
                </div>

                <div className="booking-body">
                  <p><MapPin size={14} /> {b.clientId?.destinationInterest || b.destination || "Destination not specified"}</p>
                  <p><Calendar size={14} /> {formatTravelDates(b.startDate, b.endDate)}</p>
                  <p><Users size={14} /> {b.clientId?.numberOfTravelers || b.numberOfTravelers || 1} travelers</p>
                  {agentName && agentName !== "No Agent" && (
                    <p><UserCircle size={14} /> Agent: {agentName}</p>
                  )}
                  {b.specialRequests && (
                    <p className="special-requests">Special Requests: {b.specialRequests}</p>
                  )}
                </div>

                <div className="booking-footer">
                  <div>
                    <small>Total Amount</small>
                    <strong>₹{(parseFloat(b.totalAmount) || 0).toLocaleString()}</strong>
                  </div>
                  <div className="amount-due">
                    <small>Amount Due</small>
                    <strong
                      style={{ color: amountDue > 0 ? "#f59e0b" : "#059669" }}
                    >
                      ₹{amountDue.toLocaleString()}
                    </strong>
                  </div>
                </div>

                <div className="booking-contact">
                  <small>
                    {b.clientId?.email && <span>Email: {b.clientId.email}</span>}
                    {b.clientId?.phone && <span>Phone: {b.clientId.phone}</span>}
                    {!b.clientId?.email && !b.clientId?.phone && b.contactEmail && (
                      <span>Contact: {b.contactEmail} {b.contactPhone && `| ${b.contactPhone}`}</span>
                    )}
                  </small>
                </div>

                <div className="booking-actions">
                  <button 
                    className="btn-outline btn-sm"
                    onClick={() => {
                      setSelectedBooking(b);
                      setShowEditDialog(true);
                    }}
                    disabled={actionLoading}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn-danger btn-sm"
                    onClick={() => handleDeleteBooking(b._id)}
                    disabled={actionLoading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && filteredBookings.length === 0 && (
        <div className="empty-state">
          <Calendar size={36} />
          <h3>No bookings found</h3>
          <p>{searchTerm ? "Try a different search term" : "Create your first booking to get started"}</p>
          {!searchTerm && (
            <button 
              className="btn-primary"
              onClick={() => setShowCreateDialog(true)}
              disabled={actionLoading}
            >
              Create Booking
            </button>
          )}
        </div>
      )}

      <CreateBookingDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateBooking}
        isLoading={actionLoading}
      />

      {/* Edit Booking Dialog */}
      {selectedBooking && (
        <CreateBookingDialog
          open={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedBooking(null);
          }}
          onSubmit={handleUpdateBooking}
          initialData={selectedBooking}
          isEdit={true}
          isLoading={actionLoading}
        />
      )}
    </div>
  );
}

// Updated Mock data with bookingId and bookingType
const mockBookings = [
  {
    _id: "booking001",
    bookingId: "Z2601D1",
    bookingType: "Domestic",
    clientId: {
      fullName: "John Doe",
      email: "john@example.com",
      phone: "+91 9876543210",
      destinationInterest: "Goa, India",
      numberOfTravelers: 2
    },
    agentId: {
      _id: "agent001",
      firstName: "Sarah",
      lastName: "Johnson",
      email: "sarah@example.com"
    },
    startDate: "2024-03-15",
    endDate: "2024-03-22",
    totalAmount: "50000",
    amountPaid: "25000",
    amountDue: "25000",
    bookingStatus: "Confirmed",
    paymentStatus: "Partial",
    specialRequests: "Need vegetarian meals"
  },
  {
    _id: "booking002",
    bookingId: "Z2601I1",
    bookingType: "International",
    clientId: {
      fullName: "Jane Smith",
      email: "jane@example.com",
      phone: "+91 9876543211",
      destinationInterest: "Bali, Indonesia",
      numberOfTravelers: 4
    },
    agentId: {
      _id: "agent002",
      firstName: "Michael",
      lastName: "Chen",
      email: "michael@example.com"
    },
    startDate: "2024-04-10",
    endDate: "2024-04-17",
    totalAmount: "75000",
    amountPaid: "75000",
    amountDue: "0",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
    specialRequests: ""
  }
];